import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { API, MEDIA_URL } from '../config';
import '../styles/theme.css';
import './Messages.css';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

function Avatar({ name, photo, size = 40, onClick }) {
  const url = photo
    ? (photo.startsWith('http') ? photo : `${MEDIA_URL}${photo.startsWith('/') ? '' : '/'}${photo}`)
    : null;
  return (
    <div className="msg-avatar" style={{ width: size, height: size, cursor: onClick ? 'pointer' : 'default' }} onClick={onClick} title={onClick ? `View ${name}'s profile` : name}>
      {url
        ? <img src={url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%', background: 'white', padding: '3px' }} />
        : <span>{name?.charAt(0).toUpperCase()}</span>
      }
    </div>
  );
}

function Messages({ SidebarComponent }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const token = localStorage.getItem('token');
  const userType = localStorage.getItem('user_type');

  const [resolvedUserId, setResolvedUserId] = useState(0);

  // Always fetch the real user_id from the server — never trust stale localStorage
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/auth/me/`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.id) {
          localStorage.setItem('user_id', data.id);
          setResolvedUserId(data.id);
        }
      }).catch(() => {});
  }, [token]);

  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [search, setSearch] = useState('');
  const [hoveredMsg, setHoveredMsg] = useState(null);

  // New conversation modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [seekerSearch, setSeekerSearch] = useState('');
  const [seekerResults, setSeekerResults] = useState([]);
  const [selectedSeeker, setSelectedSeeker] = useState(null);
  const [newMsgText, setNewMsgText] = useState('');
  const [startingConv, setStartingConv] = useState(false);

  const bottomRef = useRef(null);
  const pollRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/messages/conversations/`, { headers });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
        // If there's an active conversation, refresh its data too
        setActiveConv(prev => {
          if (!prev) return prev;
          const updated = data.find(c => c.id === prev.id);
          return updated || prev;
        });
      }
    } catch {}
    finally { setLoadingConvs(false); }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await fetch(`${API}/messages/conversations/${convId}/messages/`, { headers });
      if (res.ok) {
        setMessages(await res.json());
        fetchConversations();
      }
    } catch {}
    finally { setLoadingMsgs(false); }
  }, [fetchConversations]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    if (location.state?.convId && conversations.length > 0) {
      const conv = conversations.find(c => c.id === location.state.convId);
      if (conv) openConversation(conv);
    }
  }, [location.state, conversations]);

  const openConversation = (conv) => {
    setActiveConv(conv);
    setLoadingMsgs(true);
    fetchMessages(conv.id);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => fetchMessages(conv.id), 4000);
  };

  useEffect(() => () => clearInterval(pollRef.current), []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || !activeConv || sending) return;
    setSending(true);
    const content = input.trim();
    setInput('');
    try {
      const res = await fetch(`${API}/messages/conversations/${activeConv.id}/messages/`, {
        method: 'POST', headers, body: JSON.stringify({ content }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setConversations(prev => prev.map(c =>
          c.id === activeConv.id ? { ...c, last_message: content, last_message_at: msg.created_at } : c
        ));
      }
    } catch {}
    finally { setSending(false); }
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const deleteMessage = async (msgId) => {
    try {
      await fetch(`${API}/messages/messages/${msgId}/`, { method: 'DELETE', headers });
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch { alert('Failed to delete message.'); }
  };

  const deleteConversation = async (convId) => {
    if (!window.confirm('Delete this entire conversation?')) return;
    try {
      await fetch(`${API}/messages/conversations/${convId}/`, { method: 'DELETE', headers });
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (activeConv?.id === convId) { setActiveConv(null); setMessages([]); }
    } catch { alert('Failed to delete conversation.'); }
  };

  // Search seekers (recruiters search for seekers, seekers search for recruiters)
  const searchUsers = async (q) => {
    if (!q.trim()) { setSeekerResults([]); return; }
    try {
      const res = await fetch(`${API}/auth/search-users/?q=${encodeURIComponent(q)}&exclude_type=${userType}`, { headers });
      if (res.ok) setSeekerResults(await res.json());
    } catch {}
  };

  const startConversation = async () => {
    if (!selectedSeeker || !newMsgText.trim()) return;
    setStartingConv(true);
    try {
      const res = await fetch(`${API}/messages/conversations/`, {
        method: 'POST', headers,
        body: JSON.stringify({ other_user_id: selectedSeeker.id, initial_message: newMsgText.trim() }),
      });
      if (res.ok) {
        const conv = await res.json();
        await fetchConversations();
        setShowNewModal(false);
        setSeekerSearch(''); setSeekerResults([]); setSelectedSeeker(null); setNewMsgText('');
        // open the new conversation
        openConversation(conv);
      }
    } catch {}
    finally { setStartingConv(false); }
  };

  const filtered = conversations.filter(c =>
    c.other_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.job_title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="portal-layout">
      <SidebarComponent />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Messages</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme}>
              <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i>
            </button>
          </div>
        </div>

        <div className="msg-layout">
          {/* Conversations panel */}
          <div className={`msg-sidebar${activeConv ? ' mobile-hidden' : ''}`}>
            <div className="msg-sidebar-header">
              <button className="msg-new-btn" onClick={() => setShowNewModal(true)}>
                <i className="fas fa-edit"></i> New Message
              </button>
              <div className="msg-search-wrap">
                <i className="fas fa-search msg-search-icon"></i>
                <input className="msg-search" placeholder="Search conversations..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>

            <div className="msg-conv-list">
              {loadingConvs ? (
                <div className="msg-empty"><i className="fas fa-spinner fa-spin"></i></div>
              ) : filtered.length === 0 ? (
                <div className="msg-empty">
                  <i className="fas fa-comments"></i>
                  <p>No conversations yet</p>
                  <p style={{ fontSize: '0.78rem' }}>Click "New Message" to start one</p>
                </div>
              ) : filtered.map(conv => (
                <div key={conv.id} className={`msg-conv-item ${activeConv?.id === conv.id ? 'active' : ''}`} onClick={() => openConversation(conv)}>
                  <Avatar name={conv.other_name} photo={conv.other_photo} size={40} />
                  <div className="msg-conv-info">
                    <div className="msg-conv-top">
                      <span className="msg-conv-name">{conv.other_name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <span className="msg-conv-time">{timeAgo(conv.last_message_at)}</span>
                        <button className="msg-conv-delete" title="Delete conversation"
                          onClick={e => { e.stopPropagation(); deleteConversation(conv.id); }}>
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                    <div className="msg-conv-job">
                      {conv.job_title
                        ? <><i className="fas fa-briefcase"></i> {conv.job_title}</>
                        : <><i className="fas fa-user"></i> {userType === 'RECRUITER' ? 'Job Seeker' : 'HR Department'}</>
                      }
                    </div>
                    <div className="msg-conv-preview">
                      <span>{conv.last_message || 'No messages yet'}</span>
                      {conv.unread_count > 0 && <span className="msg-unread-badge">{conv.unread_count}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Chat panel */}
          <div className={`msg-chat${!activeConv ? ' mobile-hidden' : ''}`}>
            {!activeConv ? (
              <div className="msg-empty msg-empty-chat">
                <i className="fas fa-comments"></i>
                <h3>Select a conversation</h3>
                <p>Choose from the left or start a new message.</p>
              </div>
            ) : (
              <>
                <div className="msg-chat-header">
                  <button className="msg-back-btn" onClick={() => setActiveConv(null)} title="Back">
                    <i className="fas fa-arrow-left"></i>
                  </button>
                  <Avatar
                    name={activeConv.other_name}
                    photo={activeConv.other_photo}
                    size={40}
                    onClick={() => {
                      if (userType === 'RECRUITER') navigate(`/recruiter/seeker-profile/${activeConv.other_id}`);
                      else navigate(`/company/${encodeURIComponent(activeConv.other_name)}`);
                    }}
                  />
                  <div>
                    <div className="msg-chat-name">{activeConv.other_name}</div>
                    {activeConv.job_title
                      ? <div className="msg-chat-sub"><i className="fas fa-briefcase"></i> {activeConv.job_title}</div>
                      : <div className="msg-chat-sub"><i className="fas fa-user"></i> {userType === 'RECRUITER' ? 'Job Seeker' : 'HR Department'}</div>
                    }
                  </div>
                </div>

                <div className="msg-messages">
                  {loadingMsgs ? (
                    <div className="msg-empty"><i className="fas fa-spinner fa-spin"></i></div>
                  ) : messages.length === 0 ? (
                    <div className="msg-empty"><p>No messages yet. Say hello!</p></div>
                  ) : resolvedUserId === 0 ? (
                    <div className="msg-empty"><i className="fas fa-spinner fa-spin"></i></div>
                  ) : messages.map((msg, i) => {
                    const isMine = msg.sender_id === resolvedUserId;
                    const showDate = i === 0 || new Date(msg.created_at).toDateString() !== new Date(messages[i-1].created_at).toDateString();
                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <div className="msg-date-divider">
                            <span>{new Date(msg.created_at).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                          </div>
                        )}
                        <div className={`msg-bubble-wrap ${isMine ? 'mine' : 'theirs'}`}
                          onMouseEnter={() => setHoveredMsg(msg.id)}
                          onMouseLeave={() => setHoveredMsg(null)}>
                          {isMine && hoveredMsg === msg.id && (
                            <button className="msg-delete-btn" title="Delete message" onClick={() => deleteMessage(msg.id)}>
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          )}
                          <div className={`msg-bubble ${isMine ? 'mine' : 'theirs'}`}>
                            <p>{msg.content}</p>
                            <span className="msg-time">
                              {formatTime(msg.created_at)}
                              {isMine && <i className={`fas ${msg.is_read ? 'fa-check-double' : 'fa-check'} msg-read-icon`}></i>}
                            </span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                <div className="msg-input-bar">
                  <textarea className="msg-input" rows="1" placeholder="Type a message..."
                    value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} />
                  <button className="msg-send-btn" onClick={sendMessage} disabled={!input.trim() || sending}>
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* New Conversation Modal */}
      {showNewModal && (
        <div className="msg-new-modal-overlay" onClick={() => setShowNewModal(false)}>
          <div className="msg-new-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3><i className="fas fa-edit" style={{ marginRight: '0.5rem', color: 'var(--primary)' }}></i>New Message</h3>
              <button className="modal-close" onClick={() => setShowNewModal(false)}><i className="fas fa-times"></i></button>
            </div>

            <div className="form-group">
              <label>Search {userType === 'RECRUITER' ? 'job seekers' : 'recruiters & HR teams'}</label>
              <input
                type="text"
                placeholder="Type a name or email..."
                value={seekerSearch}
                onChange={e => { setSeekerSearch(e.target.value); searchUsers(e.target.value); setSelectedSeeker(null); }}
              />
              {seekerResults.length > 0 && !selectedSeeker && (
                <div className="msg-seeker-results">
                  {seekerResults.map(u => (
                    <div key={u.id} className="msg-seeker-result" onClick={() => { setSelectedSeeker(u); setSeekerSearch(u.full_name); setSeekerResults([]); }}>
                      {u.full_name}
                      <small>{u.email}</small>
                    </div>
                  ))}
                </div>
              )}
              {selectedSeeker && (
                <div style={{ fontSize: '0.8rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <i className="fas fa-check-circle"></i> {selectedSeeker.full_name} selected
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Message</label>
              <textarea rows="3" placeholder="Write your message..." value={newMsgText} onChange={e => setNewMsgText(e.target.value)} />
            </div>

            <div className="msg-new-modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowNewModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={startConversation}
                disabled={!selectedSeeker || !newMsgText.trim() || startingConv}>
                {startingConv ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Messages;
